/**
 * RateListDialog — read-only view of the series' fixed exchange-rate list (kurzovní lístek). Opened next to
 * the domestic-currency summary; shows each fixed currency's rate to the domestic currency and when it was
 * fixed. Uses the shared `Dialog`. Rates are managed automatically (fixed from the default legacy rate list
 * on first use of a currency) — this view never edits them.
 */
import { useTranslation } from 'react-i18next';

import type { FixedExchangeRateDto } from '@/api/generated/schemas';
import { Dialog, Money } from '@/shared/ui';

interface RateListDialogProps {
  open: boolean;
  onClose: () => void;
  domesticCurrency: string;
  rates: FixedExchangeRateDto[];
}

const FIXED_AT_FORMAT = new Intl.DateTimeFormat('cs-CZ', {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
});

export function RateListDialog({ open, onClose, domesticCurrency, rates }: RateListDialogProps) {
  const { t } = useTranslation('venua-planner');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width="sm"
      title={t('costScheme.rates.title')}
      subtitle={t('costScheme.rates.subtitle', { currency: domesticCurrency })}
      closeLabel={t('costScheme.rates.close')}
    >
      {rates.length === 0 ? (
        <p className="text-phx-text-muted text-phx-sm">{t('costScheme.rates.empty')}</p>
      ) : (
        <table className="text-phx-sm w-full">
          <thead>
            <tr className="text-phx-text-muted text-phx-xs text-left tracking-wide uppercase">
              <th className="pb-1 font-semibold">{t('costScheme.rates.colCurrency')}</th>
              <th className="pb-1 text-right font-semibold">{t('costScheme.rates.colRate')}</th>
              <th className="pb-1 text-right font-semibold">{t('costScheme.rates.colFixedAt')}</th>
            </tr>
          </thead>
          <tbody>
            {rates.map((rate) => (
              <tr key={rate.currency} className="border-phx-border border-t">
                <td className="py-1 font-mono font-semibold">{rate.currency}</td>
                <td className="py-1 text-right whitespace-nowrap">
                  <span className="text-phx-text-muted">1 {rate.currency} = </span>
                  <Money value={rate.rateToDomestic} currency={domesticCurrency} decimals={4} />
                </td>
                <td className="text-phx-text-muted py-1 text-right whitespace-nowrap">
                  {FIXED_AT_FORMAT.format(new Date(rate.fixedAtUtc))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Dialog>
  );
}
